import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { api } from '../lib/api';

export type ExerciseLibraryPayload = { builtIn: string[]; custom: string[] };

function mergeExerciseLists(builtIn: string[], custom: string[]): {
  merged: string[];
  lowerSet: Set<string>;
} {
  const byKey = new Map<string, string>();
  const add = (raw: string) => {
    const t = raw.trim();
    const k = t.toLowerCase();
    if (t.length < 2 || byKey.has(k)) return;
    byKey.set(k, t);
  };
  for (const x of builtIn) add(x);
  for (const x of custom) add(x);
  const merged = [...byKey.values()].sort((a, b) => a.localeCompare(b, 'en'));
  return { merged, lowerSet: new Set(byKey.keys()) };
}

const inputClass =
  'mt-1 box-border w-full min-w-0 max-w-full rounded-xl border border-white/10 bg-zinc-900/90 px-3 py-2 text-sm text-white placeholder:text-zinc-600';

const MAX_SUGGESTIONS = 14;

type Props = {
  value: string;
  onChange: (name: string) => void;
  /** Loaded once on Workouts page — avoids N fetches for N exercise rows */
  builtIn: string[];
  custom: string[];
  onCustomAdded: (canonicalName: string) => void;
  libraryFailed?: boolean;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
};

export function ExerciseNameAutocomplete({
  value,
  onChange,
  builtIn,
  custom,
  onCustomAdded,
  libraryFailed,
  disabled,
  placeholder,
  id,
}: Props) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [hl, setHl] = useState(0);

  const { merged, lowerSet } = useMemo(
    () => mergeExerciseLists(builtIn, custom),
    [builtIn, custom],
  );

  const q = value.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return [];
    const out: string[] = [];
    for (const name of merged) {
      if (name.toLowerCase().includes(q)) {
        out.push(name);
        if (out.length >= MAX_SUGGESTIONS) break;
      }
    }
    return out;
  }, [merged, q]);

  const showList = open && q.length >= 1 && filtered.length > 0 && !disabled;

  useEffect(() => {
    setHl(0);
  }, [q, showList]);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      const el = wrapRef.current;
      if (!el || !open) return;
      if (e.target instanceof Node && !el.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const pick = useCallback(
    (name: string) => {
      onChange(name);
      setOpen(false);
    },
    [onChange],
  );

  const persistCustom = useCallback(
    async (raw: string) => {
      const t = raw.trim().replace(/\s+/g, ' ');
      if (t.length < 2) return;
      if (lowerSet.has(t.toLowerCase())) return;
      try {
        const res = await api<{ ok: boolean; name: string }>('/api/exercises/custom', {
          method: 'POST',
          body: JSON.stringify({ name: t }),
        });
        const canon = res.name?.trim() || t;
        const matchesBuiltIn = builtIn.some((x) => x.toLowerCase() === canon.toLowerCase());
        if (!matchesBuiltIn) onCustomAdded(canon);
        if (canon !== value) onChange(canon);
      } catch {
        /* offline / auth — field still has their text */
      }
    },
    [builtIn, lowerSet, onChange, onCustomAdded, value],
  );

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (disabled) return;
    if (!showList) {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.currentTarget.blur();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHl((i) => (i + 1) % filtered.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHl((i) => (i - 1 + filtered.length) % filtered.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pick(filtered[hl] ?? filtered[0]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  }

  return (
    <div ref={wrapRef} className="relative min-w-0 flex-1">
      <input
        id={id}
        value={value}
        disabled={disabled}
        autoComplete="off"
        spellCheck={false}
        placeholder={placeholder}
        className={inputClass}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          setOpen(false);
          void persistCustom(value);
        }}
        onKeyDown={onKeyDown}
      />
      {libraryFailed ? (
        <p className="mt-1 text-[10px] text-amber-400/90">
          Could not load exercise suggestions — you can still type any name.
        </p>
      ) : null}
      {showList ? (
        <ul
          className="absolute z-50 mt-1 max-h-56 w-full overflow-y-auto rounded-xl border border-white/15 bg-zinc-950 py-1 shadow-xl nice-scroll"
          role="listbox"
        >
          {filtered.map((name, i) => (
            <li key={`${name}-${i}`} role="option" aria-selected={i === hl}>
              <button
                type="button"
                className={`block w-full px-3 py-2 text-left text-sm ${
                  i === hl ? 'bg-sky-600/25 text-white' : 'text-zinc-200 hover:bg-white/[0.06]'
                }`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(name)}
                onMouseEnter={() => setHl(i)}
              >
                {name}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export async function fetchExerciseLibrary(): Promise<ExerciseLibraryPayload> {
  return api<ExerciseLibraryPayload>('/api/exercises/library');
}
